import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

export default function ContactSection() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      setError(t('contact.form.err.required'));
      return;
    }
    if (formData.message.length > 500) {
      setError(t('contact.form.err.tooLong'));
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([key, val]) => body.append(key, val));
      const res = await fetch('https://readdy.ai/api/form/d748j1cprit363gl0ifg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError(t('contact.form.err.failed'));
      }
    } catch {
      setError(t('contact.form.err.network'));
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfoItems = [
    { icon: 'ri-mail-line', labelKey: 'contact.info1.label', valueKey: 'contact.info1.value' },
    { icon: 'ri-time-line', labelKey: 'contact.info2.label', valueKey: 'contact.info2.value' },
    { icon: 'ri-customer-service-2-line', labelKey: 'contact.info3.label', valueKey: 'contact.info3.value' },
  ];

  const serviceOptions = [
    { value: 'contact.opt.litho' },
    { value: 'contact.opt.xenon' },
    { value: 'contact.opt.rtp' },
    { value: 'contact.opt.epi' },
    { value: 'contact.opt.align' },
    { value: 'contact.opt.cure' },
    { value: 'contact.opt.other' },
  ];

  return (
    <section id="contact" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#1B3F7A]/20 bg-[#1B3F7A]/5 mb-6">
            <span className="w-2 h-2 rounded-full bg-[#1B3F7A] inline-block" />
            <span className="text-[#1B3F7A] text-xs tracking-widest uppercase font-medium">{t('contact.badge')}</span>
          </div>
          <p className="text-gray-500 text-base max-w-xl mx-auto leading-relaxed">{t('contact.desc')}</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-12">
          <div className="w-full lg:w-[38%]">
            <div className="rounded-2xl overflow-hidden h-56 mb-8">
              <img
                src="https://public.readdy.ai/ai/img_res/79ee84a0cd63a97a52d03f76ee8b9fbd.jpg"
                alt="ONOPTO 公司"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="flex flex-col gap-5">
              {contactInfoItems.map(item => (
                <div key={item.labelKey} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#1B3F7A]/10 flex-shrink-0">
                    <i className={`${item.icon} text-[#1B3F7A] text-base`} />
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 font-medium uppercase tracking-wide">{t(item.labelKey)}</div>
                    <div className="text-sm text-gray-700 mt-0.5 leading-snug">{t(item.valueKey)}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex items-center gap-3">
              {[
                { icon: 'ri-linkedin-line', href: '#' },
                { icon: 'ri-wechat-line', href: '#' },
                { icon: 'ri-mail-send-line', href: 'mailto:info@onopto.com' },
              ].map(s => (
                <a
                  key={s.icon}
                  href={s.href}
                  rel="nofollow"
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 hover:bg-[#1B3F7A] hover:text-white transition-all duration-200"
                >
                  <i className={`${s.icon} text-sm`} />
                </a>
              ))}
            </div>
          </div>

          <div className="w-full lg:w-[62%]">
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-20 rounded-2xl bg-[#F5F4F1]">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#1B3F7A]/10 mb-6">
                  <i className="ri-checkbox-circle-line text-[#1B3F7A] text-3xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('contact.success.title')}</h3>
                <p className="text-gray-500 text-sm max-w-sm leading-relaxed">{t('contact.success.desc')}</p>
              </div>
            ) : (
              <form
                data-readdy-form
                id="onopto-contact-form"
                onSubmit={handleSubmit}
                className="bg-[#F5F4F1] rounded-2xl p-8 flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                      {t('contact.form.name')} <span className="text-red-500">{t('contact.form.required')}</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={e => setFormData({ ...formData, name: e.target.value })}
                      placeholder={t('contact.form.namePh')}
                      className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1B3F7A] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                      {t('contact.form.email')} <span className="text-red-500">{t('contact.form.required')}</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={e => setFormData({ ...formData, email: e.target.value })}
                      placeholder={t('contact.form.emailPh')}
                      className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1B3F7A] transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                      {t('contact.form.company')}
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={e => setFormData({ ...formData, company: e.target.value })}
                      placeholder={t('contact.form.companyPh')}
                      className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1B3F7A] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                      {t('contact.form.service')}
                    </label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={e => setFormData({ ...formData, service: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200 text-sm text-gray-800 focus:outline-none focus:border-[#1B3F7A] transition-colors cursor-pointer"
                    >
                      <option value="">{t('contact.form.servicePh')}</option>
                      {serviceOptions.map(opt => (
                        <option key={opt.value} value={t(opt.value)}>{t(opt.value)}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                    {t('contact.form.message')} <span className="text-red-500">{t('contact.form.required')}</span>
                    <span className="text-gray-400 font-normal ml-2">({formData.message.length}/500)</span>
                  </label>
                  <textarea
                    name="message"
                    rows={5}
                    value={formData.message}
                    onChange={e => {
                      if (e.target.value.length <= 500) {
                        setFormData({ ...formData, message: e.target.value });
                      }
                    }}
                    placeholder={t('contact.form.messagePh')}
                    className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1B3F7A] transition-colors resize-none"
                  />
                </div>

                {error && (
                  <div className="px-4 py-3 rounded-lg bg-red-50 text-red-600 text-sm">{error}</div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3.5 bg-[#1B3F7A] text-white font-semibold rounded-full hover:bg-[#152f5e] transition-all duration-200 cursor-pointer whitespace-nowrap text-sm disabled:opacity-60"
                >
                  {submitting ? t('contact.form.submitting') : t('contact.form.submit')}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
